/**
 * TextEditModal Component for LOOPINO AI Mobile
 * Modal for editing post description text
 */

import React, { useState, useEffect, useCallback } from 'react';
import './TextEditModal.css';
import useLanguage from '../../../hooks/useLanguage';

// Close Icon component
const CloseIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Edit Icon component
const EditIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

// Save Icon component
const SaveIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2"/>
    <polyline points="17,21 17,13 7,13 7,21" stroke="currentColor" strokeWidth="2"/>
    <polyline points="7,3 7,8 15,8" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const TextEditModal = ({
  isOpen,
  onClose,
  text = '',
  onSave,
  isSaving = false
}) => {
  const { t } = useLanguage();
  const [editedText, setEditedText] = useState(text);
  const [isVisible, setIsVisible] = useState(false);

  // Handle modal visibility with animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      setIsVisible(false);
      // Restore body scroll
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setEditedText(text);
    }
  }, [isOpen, text]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!editedText.trim() || !onSave) return;

    try {
      await onSave(editedText.trim());
      // Modal will be closed by parent component on successful save
    } catch (error) {
      console.error('[TextEditModal] Save failed:', error);
    }
  }, [editedText, onSave]);

  // Handle overlay click
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  }, [onClose, isSaving]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, isSaving]);

  if (!isOpen) return null;

  return (
    <div 
      className={`text-edit-modal-overlay ${isVisible ? 'visible' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className={`text-edit-modal ${isVisible ? 'visible' : ''}`}>
        {/* Header */}
        <div className="text-edit-modal-header">
          <div className="text-edit-modal-title-section">
            <EditIcon size={24} />
            <div>
              <h2 className="text-edit-modal-title">{t('postReview.textModal.title')}</h2>
              <p className="text-edit-modal-description">{t('postReview.textModal.subtitle')}</p>
            </div>
          </div>
          <button
            className="text-edit-modal-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label={t('postReview.textModal.aria.close')}
          >
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="text-edit-modal-content">
          {/* Text Input */}
          <div className="text-edit-modal-input-section">
            <label htmlFor="text-edit" className="text-edit-input-label">
              {t('postReview.textModal.labels.description')}
            </label>
            <textarea
              id="text-edit"
              className="text-edit-modal-textarea"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              placeholder={t('postReview.textModal.placeholders.description')}
              disabled={isSaving}
              rows={6}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="text-edit-modal-footer">
          <button
            className="text-edit-modal-button primary"
            onClick={handleSave}
            disabled={!editedText.trim() || isSaving}
          >
            {isSaving ? (
              <>
                <div className="text-edit-loading-spinner" />
                {t('postReview.textModal.status.saving')}
              </>
            ) : (
              <>
                <SaveIcon size={16} />
                {t('postReview.textModal.actions.save')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TextEditModal; 
